import * as Api from '@ViewModels';
import { ILocationState } from '../index';
import { getDisplayName } from '../NameUtils';

const EntityKeys: (keyof Api.IUser | keyof Api.IContact | keyof Api.ICompany)[] = [
	'firstName',
	'lastName',
	'primaryEmail',
	'profilePic',
	'keyFactsCollection',
	'tags',
	'handle',
	'emailDomain',
	'emailDomains',
	'logoUrl',
];

/**
 * Checks if object is an entity
 * @source Original location: UiUtils.ts.backup line 200
 * @note Type safety improvement: Changed from any to unknown with type guard for better type inference
 */
export const isEntity = (entity: unknown): entity is Api.IEntity => {
	return (
		!!entity && typeof entity === 'object' && !!EntityKeys.find(x => Object.prototype.hasOwnProperty.call(entity, x))
	);
};

/**
 * Gets entity display name
 * @source Original location: UiUtils.ts.backup line 204
 */
export const getEntityDisplayName = (entity: Api.IEntity) => {
	if (entity) {
		return Object.prototype.hasOwnProperty.call(entity, 'firstName') ||
			Object.prototype.hasOwnProperty.call(entity, 'lastName') ||
			Object.prototype.hasOwnProperty.call(entity, 'email') ||
			Object.prototype.hasOwnProperty.call(entity, 'primaryEmail')
			? getDisplayName(entity as Api.IPrincipal)
			: entity.companyName;
	}
	return '';
};

/**
 * Gets entity view models from location state
 * @source Original location: UiUtils.ts.backup line 536
 */
// Note: Type safety improvement - changed from any to unknown for better type safety
export const getEntityViewModelsFromLocationState = <TViewModel = unknown, TModel = unknown>(
	locationState?: ILocationState<TViewModel, TModel>
) => {
	let entityViewModels: Api.EntityViewModel<Api.IEntity>[] = null;

	if (!!locationState && !!locationState.viewModel) {
		if (locationState.viewModel instanceof Api.OpportunityViewModel) {
			const companyModel = locationState.viewModel.company ? locationState.viewModel.company : null;
			const primaryContactModel = locationState.viewModel.primaryContact
				? locationState.viewModel.primaryContact
				: null;
			if (!!primaryContactModel || !!companyModel) {
				entityViewModels = [primaryContactModel, companyModel].filter(x => !!x);
			}
		} else if (locationState.viewModel instanceof Api.EntityViewModel) {
			entityViewModels = [locationState.viewModel];
		}
	}

	return entityViewModels;
};

/**
 * Gets mention entities from location state
 * @source Original location: UiUtils.ts.backup line 565
 */
// Note: Type safety improvement - changed from any to unknown for better type safety
export const getMentionEntitiesFromLocationState = <TViewModel = unknown, TModel extends Api.IEntity = Api.IEntity>(
	locationState?: ILocationState<TViewModel, TModel>
): Api.IEntity[] => {
	const entityViewModels = getEntityViewModelsFromLocationState(locationState);
	if (entityViewModels) {
		return entityViewModels.map(x => x.toMention());
	} else {
		// Check for opportunity FIRST before checking if it's an entity
		// This prevents the opportunity itself from being treated as an entity
		if (
			locationState?.model &&
			Object.prototype.hasOwnProperty.call(Object(locationState?.model), 'boardStage') &&
			Object.prototype.hasOwnProperty.call(Object(locationState?.model), 'primaryContact')
		) {
			const opp = locationState.model as Api.IOpportunity;
			// Return both company and primary contact if available
			const entities: Api.IEntity[] = [];
			if (opp.company) {
				entities.push(opp.company);
			}
			if (opp.primaryContact) {
				entities.push(opp.primaryContact);
			}
			if (entities.length > 0) {
				return entities;
			}
		} else if (!!locationState && !!locationState.model && !!isEntity(locationState.model)) {
			return [locationState.model];
		}
	}

	return null;
};

/**
 * Gets contact full name
 * @source Original location: UiUtils.ts.backup line 2951
 */
export const getContactFullName = (contact: Api.ContactViewModel) => {
	return [contact.firstName, contact.lastName].filter(Boolean).join(' ');
};
